import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useLogin } from "../../shared/hooks/useAuth";
import { HttpError } from "../../shared/api/client";

const Schema = z.object({
  email: z.string().email(),
  password: z.string().min(1).max(128),
});

type FormValues = z.infer<typeof Schema>;

export function LoginForm({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const login = useLogin();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: { email: "", password: "" },
  });

  const error = login.error;
  const errorMessage =
    error instanceof HttpError && error.status === 429
      ? "Too many attempts. Try again later."
      : error instanceof HttpError
        ? "Invalid credentials."
        : null;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      onSuccess();
    } catch {
      /* surfaced via login.error */
    }
  });

  return (
    <form
      onSubmit={onSubmit}
      className="w-80 space-y-3 rounded border border-line bg-bg-surface p-6"
    >
      <h1 className="text-lg font-semibold text-text">Sign in</h1>

      <label className="block text-xs text-text-muted">
        Email
        <input
          {...register("email")}
          type="email"
          autoComplete="username"
          className="mt-1 w-full rounded border border-line bg-bg-elevated px-2 py-1 text-sm text-text"
        />
        {errors.email && (
          <span className="block text-[10px] text-accent-bear">{errors.email.message}</span>
        )}
      </label>

      <label className="block text-xs text-text-muted">
        Password
        <input
          {...register("password")}
          type="password"
          autoComplete="current-password"
          className="mt-1 w-full rounded border border-line bg-bg-elevated px-2 py-1 text-sm text-text"
        />
        {errors.password && (
          <span className="block text-[10px] text-accent-bear">{errors.password.message}</span>
        )}
      </label>

      {errorMessage && <p className="text-xs text-accent-bear">{errorMessage}</p>}

      <button
        type="submit"
        disabled={login.isPending}
        className="w-full rounded bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent/90 disabled:opacity-60"
      >
        {login.isPending ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}
